import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { getHourGlassService } from "@cervval.js/client-html5-core/src/services/hourglass.service";
import { toggleClass, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO, FONT_WEIGHT, H_100, SHADOW, TEXT_TRUNCATE, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, createHtmlElement, setHTMLElementStyle } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { DatedPropertyConfigTypes, PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import { anyReallyAny, clone, objectEntries, objectKeys, objectValues, sortFields } from "@cervval.js/common/src/tools/object.tools";
import { filterString } from "@cervval.js/common/src/tools/string.tools";
import { ColumnUid } from "@cervval.js/common/src/ui/columnConfiguration/columnConfiguration";
import { CjsButton } from "../../button/button";
import { CjsDialog, DialogOptions, DialogPreConfig, DialogSize } from "../../dialog/dialog";
import { PopupPosition, ShowPopupParams, ShowPopupReturn, ShowPopupStyling, showPopup } from "../../showPopup/showPopup";
import { CjsSmartList, SmartListOptions } from "../../smartList/smartList";
import { CjsColumnConfigurator, ColumnConfiguratorInitOptions, ColumnConfiguratorOptions, columnConfiguratorSmartTableRefreshReason } from "../columnConfigurator/columnConfigurator";
import { i18n as smartTableI18n } from "../i18n/i18n";
import { SELECTION_TYPE } from "../selectionModel/smartTableSelectionModel";
import { CjsSmartTable, RenderCacheData, SmartTableCell, SmartTableColumn } from "../smartTable";
import { SmartTableCheckboxSelectorColumn } from "../smartTableCheckboxSelectorColumn";
import { getDisplayStringValue } from "../smartTableColumn.tools";
import { SmartTableSortsAvailable } from "../smartTableSort.helper";
import { I18nType, i18n } from "./i18n/i18n";

/**
 * Default options for the filters smart list
 */
export const defaultColumnExcelLikeSortFiltersSmartListOptions: Partial<SmartListOptions<string>> = {
    rowHeight: 35,
    disableHeader: false,
    selection: {
        selectionType: SELECTION_TYPE.MULTIPLE,
        enableBackgroundDisplay: false
    }
};

/** Excel like sort filter, filter value */
export type ExcelLikeFilterValue = string;

/**
 * Options of ExcelLikeSortFilter
 */
export interface ColumnExcelLikeSortFilterOptions<ItemData> {

    /** SmartTable to configure columns */
    smartTable: CjsSmartTable<ItemData>;

    /** Can sort multiple columns at the same time with a sort order */
    enableMultipleSort?: boolean;

    /** Used to override style */
    overrideCssPopup?: ShowPopupStyling;

    /** Override ExcelLikeSortFilter dialog options */
    dialogOptions?: Partial<DialogOptions>;

    /** Apply this CSS string to dialogBody's height and maxHeight */
    forceDialogHeight?: string;

    /** Override Popup options of ExcelLikeSortFilter */
    popupOptions?: Partial<ShowPopupParams>;

    /** Disable sort manipulation buttons */
    disableSort?: boolean;

    /** Disable column configuration and hide corresponding button */
    disableColumnConfiguration?: boolean;

    /** Export Table callback */
    exportTable?: (smartTable: CjsSmartTable<ItemData>) => void;

    /** Instance of the Dialog to use to display the ColumnConfigurator */
    columnConfiguratorDialog?: () => CjsDialog;
}

export type ColumnExcelLikeSortFilterConstructorOptions<ItemData> = Partial<ColumnExcelLikeSortFilterOptions<ItemData> & {
    columnConfigurator: CjsColumnConfigurator<ItemData> | { config?: ColumnConfiguratorInitOptions<ItemData>, options?: Partial<ColumnConfiguratorOptions> }
}>;

/**
 * Represent data filtered for a given column for filters states
 */
export interface ExcelLikeFilter<ItemData> {

    /** Column linked where filter is applied */
    column: SmartTableColumn<ItemData>;

    /** 
     * Selected values in string format to compare with string values in the smart table
     * ! If empty => all values are selected
     */
    selectedValues: ExcelLikeFilterValue[];

    /** Data to set in the filter smart list */
    filterData: string[];

    /** Order of filter, first column filtered => order:1, second: order:2 */
    order: number;
}

/** Components that shows excel like sort/filter popup when click header */
@CustomElement({
    name: "cjs-column-excel-like-sort-filter",
    template: require("./columnExcelLikeSortFilter.html"),
    style: require("./columnExcelLikeSortFilter.scss"),
    elementCssClasses: [FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO, H_100],
    doNotRefreshAfterInit: true,
    i18n
})
export class CjsColumnExcelLikeSortFilter<ItemData> extends WebComponent<I18nType> {

    @Injected(LOG_SERVICE)
    private _logService!: LogService;

    @TypedWebComponentOptions()
    public readonly options!: WebComponentOptions<ColumnExcelLikeSortFilterOptions<ItemData>>;

    @TypedHtmlElements({ ensureLoadedClasses: [CjsSmartList, CjsButton] })
    public components!: {
        // Buttons
        excelLikeButtonsContainer: HTMLDivElement,
        excelLikeSortAsc: CjsButton,
        excelLikeSortDesc: CjsButton,
        excelLikeClearSort: CjsButton,
        excelLikeColumnConfigurator: CjsButton,
        excelLikeExport: CjsButton,
        // Filter
        excelLikeFiltersList: HTMLDivElement,
        excelLikeFiltersButtons: HTMLDivElement,
        excelLikeFiltersOkButton: CjsButton,
        excelLikeFiltersCancelButton: CjsButton,
        excelLikeFilterSmartList: CjsSmartList<ExcelLikeFilterValue>
    };

    /** If defined, current popup displayed */
    public popup?: ShowPopupReturn;

    /** Store filters by column uid */
    public filters: Record<ColumnUid, ExcelLikeFilter<ItemData>> = {};

    /** Store column selectedValues when popup is opened, restore it if popup is cancelled */
    public selectedValuesOnOpen: string[] = [];

    /** SmartList to show filter list */
    public get filtersSmartList(): CjsSmartList<ExcelLikeFilterValue> { return this.components.excelLikeFilterSmartList; }

    /** Shortcut to smartTable where excelLikeSortFilter is installed */
    public get table(): CjsSmartTable<ItemData> { return this.options.smartTable; }

    /** Column which the click has been triggered on */
    private _clickedColumn: SmartTableColumn<ItemData> | undefined;

    /** Checkbox column of filtersSmartList */
    private _checkBoxColumn?: SmartTableCheckboxSelectorColumn<string>;

    /** Smart table i18n */
    private _smartTableI18n = smartTableI18n();

    /** Column configurator */
    public columnConfigurator: CjsColumnConfigurator<ItemData>;

    /** Uid of values column */
    public readonly VALUES_COLUMN_UID = "valuesColumn";

    /** Store column of filter values */
    public filterValuesColumn: SmartTableColumn<ExcelLikeFilterValue> = {
        uid: this.VALUES_COLUMN_UID,
        getCellValue: (cell) => cell.rowItemData,
        filterComparator: (p) => p.item != null && filterString(p.item, p.filterValue)
    };

    constructor(options?: ColumnExcelLikeSortFilterConstructorOptions<ItemData>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);

        // Can't be in the init because this one will add the component to the DOM
        this._initSmartTableListeners();

        // Display column configurator in static mode (need to click confirm to update the columns in the smart table)
        this.columnConfigurator = options?.columnConfigurator instanceof CjsColumnConfigurator ? options?.columnConfigurator : new CjsColumnConfigurator({ smartTable: this.options.smartTable, ...options?.columnConfigurator?.config }, { disableTableRefreshInConfiguratorRefresh: true, ...options?.columnConfigurator?.options });
    }

    /** @inheritdoc */
    public override init(): void {
        this._initButtonsLabels();
        this._initSmartListFilter();
        this._initListeners();
    }

    /** @inheritdoc */
    public override refresh(): void {
        const renderCacheData = {};

        // Refresh filter values table content
        this._refreshSmartListFilter(renderCacheData);
    }

    /**
     * Render smart list to display the filter values list
     */
    private _refreshSmartListFilter(renderCacheData: RenderCacheData): void {
        if (!this._clickedColumn) {
            this._logErrorColumnNotFound();
            return;
        }

        // Remove all unused filters from filters cache
        this._removeNotFilteredColumns();

        /**
         * Set data in filters cache 
         * (only compute if filterData is undefined column is not the last filtered and filterData doesn't exist in filter state)
         */
        const clickedColumnFilter = this.filters[this._clickedColumn.uid] ??= {} as ExcelLikeFilter<ItemData>;

        // Ensure all values are defined, else, init them
        clickedColumnFilter.column = this._clickedColumn;
        clickedColumnFilter.filterData ??= [];
        clickedColumnFilter.selectedValues ??= [];
        clickedColumnFilter.order ??= Number.MAX_SAFE_INTEGER;

        // Update order of filter with current filters
        clickedColumnFilter.order = this._getFilterOrder(this._clickedColumn.uid);

        // Refresh data in filter values table
        // ! Should be performed AFTER filter:'order' definition
        clickedColumnFilter.filterData = this._refreshFilterData(renderCacheData);

        // Store initial selectedValues state of clickedColumn
        this.selectedValuesOnOpen = clone(clickedColumnFilter.selectedValues, { keepArrayReferences: false });

        // Get data to put in smartList filter values
        const data = this._getFilterDisplayedValues(this._clickedColumn.uid);

        // Set data of filter values table, clear previous filters of table when re-open it
        this.filtersSmartList.setData({ data, clear: { filters: true } });

        // Disable ok button if there is 0 or 1 value in filter list
        this.components.excelLikeFiltersOkButton.options.setAndRefreshIfChanged({ disabled: data.length <= 1 });

        // Set selected values (or all if no selected values)
        this._refreshFiltersListSelection();
    }

    /**
     * Will loop each row in the smart table to retrieve all values displayed string to display in the filter list
     */
    private _refreshFilterData(renderCacheData: RenderCacheData): string[] {
        if (!this._clickedColumn) { return []; }

        const pushedValueToCache: Record<ExcelLikeFilterValue, anyReallyAny> = {};

        // Use a set to filter duplicates
        const filterStringValues = new Set<ExcelLikeFilterValue>();

        // Retrieve all other columns filtered by excelLikeSortFilter
        const otherColumnExcelLikeFilters = this._getFilteredColumns().filter(f => f.column.uid !== this._clickedColumn?.uid);

        // Look at cells string displayed values to set filter string values to display 
        for (const row of this.table.tableRows) {

            // Get cell of row for clicked column
            const clickedColumnCell = this.table.getCellByRowAndColumnObjects(row, this._clickedColumn, renderCacheData, { doNotRegisterCellInCache: true });

            // Get column excelLike filter order
            const clickedColumnFilterOrder = this.filters[this._clickedColumn.uid].order;

            // If others filters are defined, we should remove values of clickedColumn filtered by theses columns
            let isRowAlreadyFilteredByExcelLikeFilter = false;

            // Loop into all others filters defined in excelLikeFilter
            for (const otherColumnExcelLikeFilter of otherColumnExcelLikeFilters) {

                // Retrieve the row cell for other column
                const otherFilterColumnCell = this.table.getCellByRowAndColumnObjects(row, otherColumnExcelLikeFilter.column, renderCacheData, { doNotRegisterCellInCache: true });

                // Get the stringValue
                let stringValue = this.getDisplayStringValue(otherFilterColumnCell, renderCacheData);
                if (!stringValue) {// "!" operator handle "== null || === '' " for string typed values
                    stringValue = this.i18n.filterEmptyNullUndefinedValue;
                }

                // Don't take into account filter if was defined before this filter, clickedColumn filter should not be impacted by a low priority filter
                if (clickedColumnFilterOrder < otherColumnExcelLikeFilter.order) { continue; }

                // If any values selected in otherFilter and current row otherFilter column value isn't include in selected values, don't append value
                if (
                    otherColumnExcelLikeFilter.selectedValues.length &&
                    !otherColumnExcelLikeFilter.selectedValues.includes(stringValue)
                ) {
                    isRowAlreadyFilteredByExcelLikeFilter = true;
                    break;
                }
            }
            // If row already filtered, don't append row value to available values
            if (isRowAlreadyFilteredByExcelLikeFilter) { continue; }

            // Continue compute for clickedColumn value, parse it to append into list

            const cellValue = clickedColumnCell.value;
            const stringValue = this.getDisplayStringValue(clickedColumnCell, renderCacheData);

            if (Array.isArray(cellValue)) {
                const values = stringValue?.split(",")?.filter(v => v != null && v !== "");
                if (values?.length) {
                    for (const value of values) {
                        const trimmedValue = value.trim();
                        if (pushedValueToCache[trimmedValue]) { continue; }
                        filterStringValues.add(trimmedValue);
                        pushedValueToCache[trimmedValue] = trimmedValue;
                    }
                } else {
                    filterStringValues.add(this.i18n.filterEmptyNullUndefinedValue);
                    pushedValueToCache[this.i18n.filterEmptyNullUndefinedValue] = cellValue;
                }

                continue;
            }

            let stringValueToPush;
            if (!stringValue) {
                stringValueToPush = this.i18n.filterEmptyNullUndefinedValue;
            } else {
                stringValueToPush = stringValue;
            }

            filterStringValues.add(stringValueToPush);
            pushedValueToCache[stringValueToPush] = cellValue;
        }
        // Sort and remove duplicates without null/undefined values
        return [...filterStringValues].sort((aCellStringValue, bCellStringValue) => {
            // Put Oui/Yes at first position of checkbox
            const typeUid = this._clickedColumn?.propertyConfig?.typeUid;
            if (typeUid === PropertyConfigType.CHECKBOX) {
                return aCellStringValue === this._smartTableI18n.checkboxTrueStringValue ? -1 : 1;
            } else if (typeUid && DatedPropertyConfigTypes.includes(typeUid)) {
                // Initial timestamps are stored in pushedStringValueCache
                return pushedValueToCache[aCellStringValue] - pushedValueToCache[bCellStringValue];
            } else {
                // Put Blank value at the end of the list
                if (aCellStringValue === this.i18n.filterEmptyNullUndefinedValue) {
                    return 1;
                } else if (bCellStringValue === this.i18n.filterEmptyNullUndefinedValue) {
                    return -1;
                }
            }
            // Priority to custom column sort
            return this._clickedColumn?.sortComparator?.(null, aCellStringValue, null, bCellStringValue) ?? this.options.smartTable.sortHelper.sortColumnValue(aCellStringValue, bCellStringValue, this._clickedColumn) ?? 0;
        });
    }

    /**
     * Select rows in the filter values table
     */
    private _refreshFiltersListSelection(): void {
        if (!this._clickedColumn) {
            this._logErrorColumnNotFound();
            return;
        }

        // If selectedValues, get the global index to set selection in the smart list
        const selectedValues = this.filters[this._clickedColumn.uid]?.selectedValues;

        if (!selectedValues?.length) {

            // If no selected values set, we can select all rows (ex: init or all values available)
            this._checkBoxColumn?.selectAllRows();
            return;
        }

        // Select values in table
        this.filtersSmartList.selectItems(selectedValues, undefined);
    }

    /**
     * Returns displayed values in the filter, or empty array if undefined
     * @param columnUid Filtered column uid
     */
    private _getFilterDisplayedValues(columnUid: string): string[] {
        return this.filters[columnUid]?.filterData ?? [];
    }

    /** Clear filters of excelLikeSortFilter */
    public clear(): void {
        this._clickedColumn = undefined;
        this.filters = {};
    }

    /**
     * Update selectedValue of filter, from current selectedItems in filters values table
     */
    private _applyFilters(): void {
        if (!this._clickedColumn) {
            this._logErrorColumnNotFound();
            return;
        }

        const clickedColumnFilter = this.filters[this._clickedColumn.uid];
        if (!clickedColumnFilter) {
            this._logErrorColumnNotFound();
            return;
        }

        // Update selected values only if filterData contains value (if filterData is undefined or empty, it means that there is no filter to apply)
        if (clickedColumnFilter.filterData) {
            clickedColumnFilter.selectedValues = this.filtersSmartList.getSelectedItems();
        } else {
            // No filter to apply => reset selected values to delete filter from list when popup is closed
            clickedColumnFilter.selectedValues = [];
        }
    }

    /**
     * Sort column callback
     * @param sortType Type of sort to apply
     */
    private _onSortColumnButtonClick(sortType: SmartTableSortsAvailable): void {
        if (this.options.disableSort) { return; }

        if (!this._clickedColumn) {
            this._logErrorColumnNotFound();
            return;
        }

        this.options.smartTable.sortHelper.applySorts({
            sorts: [{ sortType, uid: this._clickedColumn.uid }],
            merge: Boolean(this.options.enableMultipleSort)
        });

        // Close popup
        this.popup?.removePopup("cancel");
    }

    /** Export table content into an excel file */
    private _onExportButtonClick(): void {
        getHourGlassService().encapsulate(() => {
            // Export 
            this.options.exportTable?.(this.options.smartTable);
            // Close popup
            this.popup?.removePopup("cancel");
        });
    }

    /**
     * Display the column configurator into a dialog and close excel like sort filter popup
     */
    public onColumnConfiguratorButtonClick(): void {
        if (this.options.disableColumnConfiguration) { return; }

        this.columnConfigurator.refresh();

        this.columnConfigurator.savedConfig = clone(this.columnConfigurator.columnConfiguration);

        let dialog: CjsDialog;
        const defaultOptions: Partial<DialogOptions> = {
            preConfig: DialogPreConfig.OK_CANCEL,
            title: this.i18n.buttonLabel.excelLikeColumnConfigurator,
            size: DialogSize.LARGE,
            enableHeaderButtonToggleVisibility: true,
            onConfirmButtonClick: () => {
                if (this.table.options.excelLikeSortFilter?.preventEmptyColumns !== false && this.columnConfigurator.columnConfiguration.filter(c => c.visible).length === 0) {
                    return new Promise((resolve, reject) => {
                        this.columnConfigurator.showEmptyDialog({ onDialogHide: () => { resolve(); } });
                        this.columnConfigurator.restoreDefaultColumns();
                    });
                } else {
                    // Refresh smart table with new columns configuration
                    this.options.smartTable.refresh(columnConfiguratorSmartTableRefreshReason);
                    return Promise.resolve();
                }
            },
            onCloseButtonClick: () => {
                this.columnConfigurator.restoreDefaultColumns();
                return Promise.resolve();
            }
        };

        const options = { ...defaultOptions, ...this.options.dialogOptions };
        if (this.options.columnConfiguratorDialog) {
            dialog = this.options.columnConfiguratorDialog();
            dialog.options.setAndRefreshIfChanged(options);
        } else {
            dialog = new CjsDialog(options);
        }

        const height = this.options.forceDialogHeight ?? "50vh";
        setHTMLElementStyle(dialog.components.dialogBody, {
            height,
            maxHeight: height
        });

        if (this.columnConfigurator) {
            dialog.addToBody(this.columnConfigurator);
        }

        // Close popup 
        // ! Important to cancel it and not close it: to remove useless modifications in current popup
        this.popup?.removePopup("cancel");
    }

    /**
     * Apply filters to the smart table and update filter cache
     */
    private _onClickFilterOkButton(): void {
        if (!this._clickedColumn) {
            this._logErrorColumnNotFound();
            return;
        }


        // Apply filter into selectedValues
        this.updateFilterSelectedValues();

        // Filter table, will refresh table
        this.options.smartTable.filterHelper.filterTable({ reason: "excelLikeSortFilter" });

        // Remove not filtered columns from filters cache
        this._removeNotFilteredColumns();

        // Emit event
        this.options.smartTable.excelLikeFilterEvent.emit();

        // Close popup
        this.popup?.removePopup("close");
    }

    /**
     * Render color cells as color tile, else render string value
     * @param cell Cell to render
     */
    public defaultFilterSmartListRenderCell(cell: SmartTableCell<string>): AcceptedHtmlElementContent {
        const cellStringValue = String(cell.stringValue ?? cell.value);

        // Render color tile if property config type is color
        if (this._clickedColumn?.propertyConfig?.typeUid === PropertyConfigType.COLOR) {
            return this.filtersSmartList.colorCellBuilder(cell, cellStringValue);
        }

        // Default render the string value
        return createHtmlElement("div", {
            cssClass: [W_100, TEXT_TRUNCATE],
            innerHTML: cellStringValue,
            attributes: { title: cellStringValue }
        });
    }

    /** 
     * Apply filters currently set in excelLikeSortFilter to smartTable
     * ! Will not refresh smartTable, only update "displayed" field of smartTable rows
     */
    public updateFilterSelectedValues(): void {
        // If column is not set, no need to filter
        if (!this._clickedColumn) { return; }

        // Apply filters in the smart table
        this._applyFilters();

        // Remove useless filters
        this._removeNotFilteredColumns();
    }


    /**
     * Show popup
     * @param event Event triggered when click column header
     */
    public show(event: MouseEvent, col: SmartTableColumn<ItemData>): void {
        // To do the sort/filter on the column
        this._clickedColumn = col;
        if (!this._clickedColumn) {
            this._logErrorColumnNotFound();
            return;
        }

        // Refresh buttons
        this._refreshButtons();

        // Remove popup if already displayed
        if (this.popup) {
            this.popup.removePopup("close");
        }

        // Get bounding rect of the column header to set the absolute position of the popup
        const columnBoundingRect = this._clickedColumn.headerHtml?.getBoundingClientRect();

        // top is at the middle position of the column header
        const absoluteTopPosition = columnBoundingRect ? columnBoundingRect.top + columnBoundingRect?.height : event.clientY;

        const columnExcelLikeSortFilterHeight = 550;

        const cssPopup: ShowPopupStyling = {
            cssClass: [
                SHADOW.S10,
                ...this.options.overrideCssPopup?.cssClass ?? []
            ],
            style: {
                width: "350px",
                height: toPx(columnExcelLikeSortFilterHeight),
                border: "solid 2px #aaa",
                borderRadius: "5px",
                ...this.options.overrideCssPopup?.style
            }
        };

        // Display show popup
        this.popup = showPopup({
            parent: document.body,
            popupMode: "direct",
            event,
            ...this.options.popupOptions,
            elementToShow: this,
            disablePopupInDialog: true,
            popupPosition: PopupPosition.BOTTOM_START,
            absolutePosition: {
                top: absoluteTopPosition,
                // left is at the left position of the column header
                left: columnBoundingRect ? columnBoundingRect.left : event.clientX
            },
            cssPopup,
            onPopupCancel: () => {
                if (this._clickedColumn && this.filters[this._clickedColumn.uid]) {
                    this.filters[this._clickedColumn.uid].selectedValues = this.selectedValuesOnOpen;
                    this._removeNotFilteredColumns();
                }
            },
            onPopupHide: () => {// Still remove popup reference
                if (this.popup) { this.popup = undefined; }
                this.options.popupOptions?.onPopupHide?.();
            }
        });

        this.refresh();
    }

    /**
     * Returns display string value of the cell
     * @param cell Cell of the smart table to filter
     */
    public getDisplayStringValue(cell: SmartTableCell<ItemData>, renderCacheData: RenderCacheData | undefined): string | undefined {
        return getDisplayStringValue(cell, this.options.smartTable, renderCacheData);
    }


    /** 
     * Init smartList 
     */
    private _initSmartListFilter(): void {
        // Build smart list
        this.filtersSmartList.options.setWithoutRefresh({
            ...defaultColumnExcelLikeSortFiltersSmartListOptions,
            columns: [this.filterValuesColumn],
            renderCell: (cell) => this.defaultFilterSmartListRenderCell(cell),

            // Classic table
            parentContainerHasNoHeightConstraint: true,
            scrollBar: "standard"
        });

        // Append checkbox column to table
        this._checkBoxColumn = new SmartTableCheckboxSelectorColumn(this.filtersSmartList);
        // Add checkbox column
        this._checkBoxColumn.insertFirst();
        this._checkBoxColumn.width = "50px";
    }

    /**
     * Refresh buttons
     */
    private _refreshButtons(): void {

        // Check if sort is active on the current column to put the current sort button in bold
        const activeSort = this.options.smartTable.sortHelper.activeSortedColumns.find(sort => sort.column.uid === this._clickedColumn?.uid);
        toggleClass(this.components.excelLikeSortAsc.button, activeSort?.sortType === "ascending", FONT_WEIGHT.BOLD);
        toggleClass(this.components.excelLikeSortDesc.button, activeSort?.sortType === "descending", FONT_WEIGHT.BOLD);

        // Hide buttons if sort is disabled
        toggleVisible(this.components.excelLikeSortDesc, !this.options.disableSort);
        toggleVisible(this.components.excelLikeSortAsc, !this.options.disableSort);
        toggleVisible(this.components.excelLikeClearSort, !this.options.disableSort);

        // Hide button if column configuration is disabled
        toggleVisible(this.components.excelLikeColumnConfigurator, !this.options.disableColumnConfiguration);


        // Hide button if export is not configured
        toggleVisible(this.components.excelLikeExport, this.options.exportTable != null);
    }


    /**
     * Init listeners for smart table events
     */
    private _initSmartTableListeners(): void {
        const smartTable = this.options.smartTable;
        if (!smartTable) { return; }

        // Define interaction to open popup (left or right)
        const interaction = smartTable.options.excelLikeSortFilter?.clickInteraction ?? "right";

        // If left, override sort behavior
        if (interaction === "left") {
            smartTable.sortHelper.sortClickEvent.on(e => this.show(e.detail.mouseEvent, e.detail.col));
        } else {
            // Default behavior, open popup on right click
            smartTable.components.tableHead.addEventListener("contextmenu", (e) => {
                e.preventDefault();

                // Retrieve clicked column uid
                const colUid = (e.target as HTMLElement).closest("th[col]")?.getAttribute("col");
                if (!colUid) {
                    this._logErrorColumnNotFound();
                    return;
                }

                // Get column by uid and show the popup
                const col = this.options.smartTable.options.columns.find(c => c.uid === colUid);
                if (col) {
                    this.show(e, col);
                }
            });
        }
    }

    /**
     * Init click on buttons
     */
    private _initListeners(): void {
        this.components.excelLikeSortAsc.clickEvent.on(_ => this._onSortColumnButtonClick("ascending"));
        this.components.excelLikeSortDesc.clickEvent.on(_ => this._onSortColumnButtonClick("descending"));
        this.components.excelLikeClearSort.clickEvent.on(_ => this._onSortColumnButtonClick("none"));
        this.components.excelLikeColumnConfigurator.clickEvent.on(_ => this.onColumnConfiguratorButtonClick());
        this.components.excelLikeExport.clickEvent.on(_ => this._onExportButtonClick());
        this.components.excelLikeFiltersOkButton.clickEvent.on(_ => this._onClickFilterOkButton());
        this.components.excelLikeFiltersCancelButton.clickEvent.on(_ => this.popup?.removePopup("cancel"));


        // When unselect all rows with checkbox header
        this._checkBoxColumn?.allRowsUnselectEvent.on(_ => {
            // Disable ok button when unselect all rows in 
            this.components.excelLikeFiltersOkButton.options.setAndRefreshIfChanged({ disabled: true });
        });

        // When select all rows with checkbox header
        this._checkBoxColumn?.allRowsSelectEvent.on(_ => {
            if (!this._clickedColumn) {
                this._logErrorColumnNotFound();
                return;
            }

            // Define no selected values in filter cache <=> All values are selected
            this.filters[this._clickedColumn.uid].selectedValues = [];

            const nbrFilterDisplayedValues = this._getFilterDisplayedValues(this._clickedColumn.uid).length;
            // Disable ok button if there is 0 or 1 value in filter list
            this.components.excelLikeFiltersOkButton.options.setAndRefreshIfChanged({
                disabled: nbrFilterDisplayedValues <= 1
            });
        });

        // When click on filter value
        this.filtersSmartList.rowClickEvent.on(_ => {
            if (!this._clickedColumn) {
                this._logErrorColumnNotFound();
                return;
            }

            const nbrFilterDisplayedValues = this._getFilterDisplayedValues(this._clickedColumn.uid).length;
            // Disable ok button if there is 0 or 1 value in filter list or if nothing has been selected
            this.components.excelLikeFiltersOkButton.options.setAndRefreshIfChanged({
                disabled: nbrFilterDisplayedValues <= 1 || this.filtersSmartList.getSelectedRows().length === 0
            });

        });
    }

    /**
     * Init labels for buttons with i18n
     */
    private _initButtonsLabels(): void {
        const i18nButtonLabel = this.i18n.buttonLabel;
        this.components.excelLikeSortAsc.options.setAndRefreshIfChanged({ label: i18nButtonLabel.excelLikeSortAsc });
        this.components.excelLikeSortDesc.options.setAndRefreshIfChanged({ label: i18nButtonLabel.excelLikeSortDesc });
        this.components.excelLikeClearSort.options.setAndRefreshIfChanged({ label: i18nButtonLabel.excelLikeClearSort });
        this.components.excelLikeColumnConfigurator.options.setAndRefreshIfChanged({ label: i18nButtonLabel.excelLikeColumnConfigurator });
        this.components.excelLikeExport.options.setAndRefreshIfChanged({ label: i18nButtonLabel.excelLikeExport });
        this.components.excelLikeFiltersOkButton.options.setAndRefreshIfChanged({ label: i18nButtonLabel.excelLikeFiltersOkButton });
        this.components.excelLikeFiltersCancelButton.options.setAndRefreshIfChanged({ label: i18nButtonLabel.excelLikeFiltersCancelButton });
    }

    /**
     * Returns filtered columns by excelLikeSortFilter, ordered by 'order'
     */
    private _getFilteredColumns(): ExcelLikeFilter<ItemData>[] {
        const filteredColumns = objectValues(this.filters).filter(f => this.isColumnFiltered(f.column.uid));
        sortFields(filteredColumns, "order");
        return filteredColumns;
    }

    /**
     * Returns true if column is filtered by excelLikeSortFilter
     */
    public isColumnFiltered(columnUid: ColumnUid): boolean {
        const filter = this.filters[columnUid];
        if (!filter) { return false; }

        return Boolean(filter.selectedValues.length && filter.selectedValues.length !== filter.filterData?.length);
    }

    /**
     * Remove columns filter who don't have any filter applied
     * Will update orders of all filters if needed
     */
    private _removeNotFilteredColumns(): void {
        // Used for performance, avoid useless compute
        let columnWasRemoved = false;

        for (const columnUid of objectKeys(this.filters)) {
            if (!this.isColumnFiltered(columnUid)) {// if column is not filtered anymore, remove it from filters record
                delete this.filters[columnUid];
                columnWasRemoved = true;
            }
        }

        if (columnWasRemoved) { // Update filters order if a column was remove
            for (const [columnUid, filter] of objectEntries(this.filters)) {
                filter.order = this._getFilterOrder(columnUid);
            }
        }
    }

    /** Get up to date filter order with column uid */
    private _getFilterOrder(columnUid: ColumnUid): number {
        const filteredColumns = objectValues(this.filters);
        sortFields(filteredColumns, "order");// Sort columns by order

        filteredColumns.forEach((f, i) => f.order = i + 1);// Shrink orders to continue sequence

        // If filter already exist for this column, don't change order or filter, return updated value
        const columnFilter = filteredColumns.find(c => c.column.uid === columnUid);
        if (columnFilter) { return columnFilter.order; }

        // if new filter, increment order of last column
        const lastColumnOrder = filteredColumns.at(-1)?.order;
        if (lastColumnOrder != null) { return lastColumnOrder + 1; }

        return 1;// If no columns, order should return 1

    }

    /**
     * Show console error
     */
    private _logErrorColumnNotFound(): void {
        this._logService.error("Column to sort is not set");
    }
}